import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, HelpCircle } from 'lucide-react';

export default function BunkerSupport() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Support</h1>
            <p className="text-zinc-400 text-lg">Bunker - Whiskey Collection & Tasting Journal</p>
          </div>

          {/* Contact Card */}
          <div className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-8 mb-10">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-zinc-800 rounded-lg">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-2">Contact Us</h2>
                <p className="text-zinc-400 mb-4">
                  Have a question, feedback, or need help with Bunker? We're here to help!
                </p>
                <a
                  href="mailto:parkerdevwork@gmail.com?subject=Bunker Support"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  parkerdevwork@gmail.com
                </a>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-white flex items-center gap-2">
              <HelpCircle className="w-6 h-6" />
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">How does barcode scanning work?</h3>
                <p className="text-zinc-400">
                  Point the camera at the UPC on the back of a bottle. Bunker checks its built-in
                  catalog first, then public product databases, to identify it. If a barcode isn't
                  recognized, just search the bottle by name — the app remembers the match, so
                  scanning that bottle again is instant. No photos are ever recorded or uploaded.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Do I need to create an account?</h3>
                <p className="text-zinc-400">
                  No. Your collection, pours, and tasting notes live on your device, and the app
                  works completely offline. An optional account (email + password) exists only to
                  power the premium Friends and Cloud Backup features — and even then, friends can
                  never see what you paid. Only your own private Cloud Backup ever stores your
                  full data.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">What's in Bunker Premium, and how do I cancel?</h3>
                <p className="text-zinc-400">
                  Premium is a monthly subscription that adds Friends & shared bars, Buy Next
                  bottle suggestions, Barrel Spin, and cloud backup. It's billed through your
                  Apple ID and renews automatically — manage or cancel anytime in your device
                  Settings → Apple ID → Subscriptions. Everything else in the app stays free.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">How does "Pour Tonight" pick bottles?</h3>
                <p className="text-zinc-400">
                  Tell it the mood — nightcap, celebrating, guests over, feeling adventurous, or
                  treat yourself — and it deals three picks from bottles you actually own. It
                  weighs flavor fit, what's already open, fill levels, your ratings, and how long
                  a bottle has been neglected, with the best match on top and a reason for each.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">What can my friends see?</h3>
                <p className="text-zinc-400">
                  Only what you explicitly share, and only after you've accepted each other as
                  friends. Sharing is off by default. When it's on, friends see bottle names,
                  types, proofs, your ratings, and fill levels — never what you paid, your
                  estimated values, store names, hunt stories, or photos. Turning sharing off
                  removes your shared shelf from the cloud.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Can I back up or move my data?</h3>
                <p className="text-zinc-400">
                  Yes, two ways. Settings → Your data exports everything (collection, pours,
                  tastings, hunt list) as a single JSON file you can import on any device. Premium
                  members can also use Cloud Backup, which keeps a private copy tied to your
                  account for one-tap restore. Photos stay on your device either way.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">A bottle is missing from the catalog. What do I do?</h3>
                <p className="text-zinc-400">
                  Use manual entry on the Add screen — name, distillery, type, proof, age, and
                  price. It gets handsome generated label art and works everywhere in the app,
                  including Pour Tonight and stats. You can also email us the bottle and we'll
                  consider adding it to the catalog.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">How do I delete my account?</h3>
                <p className="text-zinc-400">
                  In the app: Settings → Account → Delete account. This permanently removes your
                  account, friendships, shared shelves, and cloud backup from our servers. The
                  bunker on your phone is untouched.
                </p>
              </div>

              <div className="bg-zinc-900/30 border border-zinc-800/30 rounded-lg p-6">
                <h3 className="text-lg font-medium text-white mb-2">Is my data safe?</h3>
                <p className="text-zinc-400">
                  Yes. Without an account we collect nothing at all, and with one we store only
                  what friends and backups need. For the full picture, please read our{' '}
                  <Link
                    to="/bunker/privacy-policy"
                    className="text-amber-400 hover:text-amber-300 underline"
                  >
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 Bunker. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
